import {
  InteractionRequiredAuthError,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import axios from 'axios';
import { loginRequest } from '../auth/msal-config';

/**
 * Registers an axios request interceptor that acquires a fresh B2C access token
 * via acquireTokenSilent() and attaches it to every authenticated request.
 *
 * If the silent refresh fails with an interaction-required error, it triggers
 * a redirect login so the user can re-authenticate.
 */
export function registerMsalAxiosInterceptor(
  msalInstance: IPublicClientApplication,
): void {
  axios.interceptors.request.use(async (config) => {
    const account = msalInstance.getActiveAccount();

    // Only attach token for requests that already have an Authorization header
    // (i.e. authenticated API calls). Unauthenticated calls are left untouched.
    if (!account || !config.headers?.Authorization) {
      return config;
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      config.headers.Authorization = `Bearer ${response.accessToken}`;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await msalInstance.acquireTokenRedirect({
          ...loginRequest,
          account,
        });
      }
      throw error;
    }

    return config;
  });
}
